import type { RiskCoverVersion } from '@festina/life-general-types/life'
import { dateUtils, notEmpty, type Optional } from '@festina-life/core'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import {
  Card,
  CardAutoLayout,
  CardBody,
  CardHeader,
  CardItem,
} from '@festina-life/ui/structure'

import { useCommitmentVersion } from '../../hooks/useCommitmentVersion'
import { withLifeErrorBoundary } from '../../libs/withLifeErrorBoundary'
import { CoverCard } from './CoverCard'

type LifeInsuranceEvent =
  RiskCoverVersion['lifeInsuranceEvents'] extends Optional<(infer T)[]>
    ? NonNullable<T>
    : never

function CoverCardListInternal() {
  const { currentCommitmentVersion: commitmentVersion } = useCommitmentVersion()
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()

  const riskCovers = commitmentVersion?.aggregatedPolicy?.riskCoverVersions

  const riskCoverGroups: Record<
    string,
    { events: LifeInsuranceEvent[]; covers: RiskCoverVersion[] }
  > = {}
  riskCovers?.filter(notEmpty).forEach((cover) => {
    const events = (cover?.lifeInsuranceEvents?.slice() ?? []).filter(notEmpty)
    events.sort()

    const key = events.join(',')

    if (riskCoverGroups[key] === undefined) {
      riskCoverGroups[key] = { events, covers: [] }
    }
    riskCoverGroups[key].covers.push(cover)
  })

  return (
    <CardAutoLayout>
      {Object.entries(riskCoverGroups).map(([key, { events, covers }]) => (
        <Card key={key}>
          <CardHeader
            title={events
              .map((e) => translateEnum('life.enum.LifeInsuranceEvent', e))
              .join(', ')}
          />

          {covers?.map((s) => (
            <CardBody key={s?.coverKey}>
              <CoverCard
                key={s?.coverKey}
                id={s?.coverKey ?? ''}
                name={s?.productName ?? ''}
                amount={
                  s?.riskBenefitSpecVersion?.amountSpecVersion?.currentAmount
                }
              >
                {s.riskEndDate && (
                  <CardItem
                    title={translate('cover.riskEndDate')}
                    value={dateUtils.dateAsStr(s?.riskEndDate)}
                  />
                )}
              </CoverCard>
            </CardBody>
          ))}
        </Card>
      ))}
    </CardAutoLayout>
  )
}

export const CoverCardList = withLifeErrorBoundary(CoverCardListInternal)
